import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from "lucide-react";
import ActionButton from "../../components/ui/ActionButton";
import AdvancedFilters from "../../components/ui/AdvancedFilters";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { PageHeading } from "../../components/ui/PageHeading";
import PaginatedSelect from "../../components/ui/PaginatedSelect";
import SearchInput from "../../components/ui/SearchInput";
import { useToast } from "../../components/ui/toast/ToastProvider";
import { useLazyGetItemsQuery } from "../../features/items/itemApi";
import {
  useAddItemRoutingSeriesMutation,
  useAddRoutingParameterMutation,
  useDeleteItemRoutingMutation,
  useGetItemRoutingsQuery,
  useLazyGetItemRoutingsQuery,
  useUpdateItemRoutingSeriesMutation,
} from "../../features/itemRoutings/itemRoutingApi";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import RoutingOperationFields from "../masters/RoutingOperationFields";
import {
  assertSuccessfulResponse,
  getRequestErrorMessage,
  getResponseMessage,
} from "../../services/apiMessages";
import MasterTable from "../masters/MasterTable";
import { withAuditFilters } from "../masters/filterMetadata";
import {
  buildNameCodeIdLabel,
  getPlantLabel,
  getItemLabel,
  getOperationLabel,
  renderPlantCell,
} from "../masters/masterMetadata";
import useResolvedFilterFields from "../masters/useResolvedFilterFields";
import ParameterDialog from "./ParameterDialog";
import OperationParameters from "./OperationParameters";

const itemRoutingFilterFields = withAuditFilters([
  { name: "item_routing_id", label: "Routing ID" },
  { name: "item_id", label: "Item", optionSource: "items" },
  { name: "operation_id", label: "Operation", optionSource: "operations" },
  {
    name: "position",
    label: "Position",
    type: "number",
    min: 1,
    step: 1,
  },
]);

const searchFields = [
  "item_routing_id",
  "plant_id",
  "item_id",
  "operation_id",
  "position",
  (row, metaData) => getPlantLabel(row, metaData),
  (row, metaData) => getItemLabel(row, metaData),
  (row, metaData) => getOperationLabel(row, metaData),
];

const getRecordUid = (row) => row?.uid || row?._id || row?.id || row?.item_routing_id;

const isSameRoutingRecord = (first, second) =>
  Boolean(
    first &&
      second &&
      ((first.id && second.id && String(first.id) === String(second.id)) ||
        (first.item_routing_id &&
          second.item_routing_id &&
          String(first.item_routing_id) === String(second.item_routing_id)))
  );

const getSearchValue = (row, fieldName, metaData) => {
  const value =
    typeof fieldName === "function" ? fieldName(row, metaData) : row?.[fieldName];
  if (typeof value === "boolean") return value ? "yes true active" : "no false inactive";
  return value ?? "";
};

const createRouteRow = (source = {}) => ({
  key:
    source.id ||
    source.item_routing_id ||
    `${Date.now()}_${Math.random().toString(36).slice(2)}`,
  id: source.id,
  item_routing_id: source.item_routing_id,
  operation_id: source.operation_id || "",
  relation_type: source.relation_type || "",
  parameters: [],
});

const hydrateRouteRows = (routes = []) => {
  const rows = [...routes]
    .sort((first, second) => (first?.position || 0) - (second?.position || 0))
    .map(createRouteRow);

  return rows.length ? rows : [createRouteRow()];
};

const buildOptionsByPlant = (rows, idField, labelConfig) => {
  const optionsByPlant = new Map();

  (rows || []).forEach((row) => {
    if (!row?.plant_id || !row?.[idField]) return;

    const plantId = String(row.plant_id);
    const currentOptions = optionsByPlant.get(plantId) || [];
    currentOptions.push({
      value: String(row[idField]),
      label: buildNameCodeIdLabel(labelConfig(row)),
    });
    optionsByPlant.set(plantId, currentOptions);
  });

  return optionsByPlant;
};

const findDuplicateValues = (values) => {
  const seen = new Set();
  const duplicates = new Set();

  values.forEach((value) => {
    if (seen.has(value)) {
      duplicates.add(value);
      return;
    }
    seen.add(value);
  });

  return [...duplicates];
};

const buildSeriesPayload = ({ plantId, itemId, operationRows }) => ({
  plant_id: plantId,
  item_id: itemId,
  operations: operationRows.map((row, index) => ({
    ...(row.id ? { id: row.id } : {}),
    ...(row.item_routing_id ? { item_routing_id: row.item_routing_id } : {}),
    operation_id: row.operation_id,
    position: index + 1,
  })),
});

const ItemRoutingSeriesDialog = ({
  open,
  sourceRow,
  selectedPlantId: commonPlantId,
  itemOptionsByPlant,
  isLoadingItems,
  onClose,
  onLoadOptions,
  onLoadRoute,
  onSave,
  isSaving,
}) => {
  const [formData, setFormData] = useState({ plant_id: commonPlantId || "", item_id: "" });
  const [operationRows, setOperationRows] = useState([createRouteRow()]);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [parameterRowKey, setParameterRowKey] = useState(null);
  const [hasSavedRouting, setHasSavedRouting] = useState(false);
  const parameterRow = operationRows.find((row) => row.key === parameterRowKey);

  const isEdit = Boolean(sourceRow);
  const selectedPlantId = formData.plant_id ? String(formData.plant_id) : "";
  const itemOptions = useMemo(
    () => itemOptionsByPlant.get(selectedPlantId) || [],
    [itemOptionsByPlant, selectedPlantId]
  );

  useEffect(() => {
    if (open) onLoadOptions();
  }, [open, onLoadOptions]);

  useEffect(() => {
    if (!open) return undefined;

    setErrorMessage("");
    setParameterRowKey(null);
    setHasSavedRouting(false);

    if (!sourceRow?.plant_id || !sourceRow?.item_id) {
      setFormData({ plant_id: commonPlantId || "", item_id: "" });
      setOperationRows([createRouteRow()]);
      setIsLoadingRoute(false);
      return undefined;
    }

    let isCancelled = false;
    setFormData({
      plant_id: String(sourceRow.plant_id),
      item_id: String(sourceRow.item_id),
    });
    setOperationRows([createRouteRow(sourceRow)]);
    setIsLoadingRoute(true);

    onLoadRoute(sourceRow.plant_id, sourceRow.item_id)
      .then((routes) => {
        if (!isCancelled) {
          setOperationRows(hydrateRouteRows(routes));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setErrorMessage("Unable to load item routing.");
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingRoute(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [commonPlantId, onLoadRoute, open, sourceRow]);

  const handleItemChange = async (itemId) => {
    setFormData((current) => ({ ...current, item_id: itemId }));
    setErrorMessage("");
    setHasSavedRouting(false);

    if (!formData.plant_id || !itemId) {
      setOperationRows([createRouteRow()]);
      return;
    }

    setIsLoadingRoute(true);
    try {
      const routes = await onLoadRoute(formData.plant_id, itemId);
      setOperationRows(hydrateRouteRows(routes));
    } catch (error) {
      setOperationRows([createRouteRow()]);
      setErrorMessage("Unable to load item routing.");
    } finally {
      setIsLoadingRoute(false);
    }
  };

  const handleOperationChange = (rowKey, operationId) => {
    setOperationRows((current) =>
      current.map((row) =>
        row.key === rowKey && row.operation_id !== operationId
          ? { ...row, operation_id: operationId, parameters: [] }
          : row
      )
    );
    setErrorMessage("");
  };

  const handleAddOperation = () => {
    setOperationRows((current) => [...current, createRouteRow()]);
    setErrorMessage("");
  };

  const handleAddParameter = (parameter) => {
    setOperationRows((current) => current.map((row) =>
      row.key === parameterRowKey
        ? {
            ...row,
            parameters: [...row.parameters, {
              ...parameter,
              key: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            }],
          }
        : row
    ));
    setParameterRowKey(null);
  };

  const handleRemoveParameter = (rowKey, parameterKey) => {
    setOperationRows((current) => current.map((row) =>
      row.key === rowKey
        ? { ...row, parameters: row.parameters.filter((entry) => entry.key !== parameterKey) }
        : row
    ));
  };

  const handleParameterSaved = (key) => {
    setOperationRows((current) => current.map((row) => ({
      ...row,
      parameters: row.parameters.map((parameter) =>
        parameter.key === key ? { ...parameter, saved: true } : parameter
      ),
    })));
  };

  const handleRemoveOperation = (rowKey) => {
    setOperationRows((current) => {
      const nextRows = current.filter((row) => row.key !== rowKey);
      return nextRows.length ? nextRows : [createRouteRow()];
    });
    setErrorMessage("");
  };

  const handleMoveOperation = (index, direction) => {
    setOperationRows((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.length) return current;

      const nextRows = [...current];
      [nextRows[index], nextRows[targetIndex]] = [
        nextRows[targetIndex],
        nextRows[index],
      ];
      return nextRows;
    });
    setErrorMessage("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.plant_id) {
      setErrorMessage("Plant is required.");
      return;
    }
    if (!formData.item_id) {
      setErrorMessage("Item is required.");
      return;
    }

    const selectedOperationRows = operationRows.filter((row) => row.operation_id);
    if (selectedOperationRows.length !== operationRows.length) {
      setErrorMessage("Operation is required for every routing position.");
      return;
    }

    const duplicateOperationIds = findDuplicateValues(
      operationRows.map((row) => String(row.operation_id))
    );
    if (duplicateOperationIds.length) {
      setErrorMessage("Each operation can be selected only once.");
      return;
    }

    onSave(
      buildSeriesPayload({
        plantId: formData.plant_id,
        itemId: formData.item_id,
        operationRows,
      }),
      isEdit || hasSavedRouting || operationRows.some((row) => row.id || row.item_routing_id),
      {
        parameters: operationRows.flatMap((row) =>
          row.parameters.filter((parameter) => !parameter.saved).map((parameter) => ({
            ...parameter,
            plant_id: formData.plant_id,
            item_id: formData.item_id,
            operation_id: row.operation_id,
          }))
        ),
        onRoutingSaved: () => setHasSavedRouting(true),
        onParameterSaved: handleParameterSaved,
      }
    );
  };

  const itemPlaceholder = !formData.plant_id
    ? "Select plant first"
    : isLoadingItems
    ? "Loading items..."
    : "Select item";

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isSaving && onClose()}>
      <DialogContent className="sm:max-w-[760px]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>
              {isEdit ? "Edit Item Routing" : "Add Item Routing"}
            </DialogTitle>
            <ActionButton action="cancel" iconOnly variant="ghost" onClick={onClose} disabled={isSaving} />
          </div>
        </DialogHeader>

        <form className="mt-2 space-y-5" onSubmit={handleSubmit}>
          <fieldset disabled={isSaving} className="min-w-0 space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="sm:col-span-2">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  Item
                </span>
                <PaginatedSelect
                  value={formData.item_id}
                  onChange={handleItemChange}
                  options={itemOptions}
                  disabled={
                    isEdit ||
                    hasSavedRouting ||
                    isLoadingRoute ||
                    !formData.plant_id ||
                    (isLoadingItems && itemOptions.length === 0)
                  }
                  placeholder={itemPlaceholder}
                />
              </label>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                <h2 className="text-sm font-semibold text-slate-900">Operation Series</h2>
                <ActionButton
                  action="add"
                  icon={Plus}
                  label="Add Operation"
                  type="button"
                  onClick={handleAddOperation}
                  disabled={!formData.plant_id || isLoadingRoute}
                />
              </div>

              <div className="space-y-2">
                {operationRows.map((row, index) => (
                  <div
                    key={row.key}
                    className="grid gap-2 rounded-md border border-slate-100 bg-white px-3 py-3 sm:grid-cols-[40px_minmax(0,1fr)_auto] sm:items-center"
                  >
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
                      <GripVertical className="h-4 w-4 text-slate-300" />
                      <span>{index + 1}</span>
                    </div>

                    <RoutingOperationFields
                      plantId={selectedPlantId}
                      row={row}
                      index={index}
                      selectedOperationIds={operationRows.map((entry) => entry.operation_id)}
                      onOperationChange={(operationId) => handleOperationChange(row.key, operationId)}
                      onRelationChange={(relationType) => setOperationRows((current) => current.map((entry) => entry.key === row.key ? { ...entry, relation_type: relationType, operation_id: "", parameters: [] } : entry))}
                      disabled={isSaving || isLoadingRoute || !formData.item_id}
                    />

                    <div className="flex items-center justify-end gap-1.5">
                      <ActionButton
                        action="add"
                        label="Add Parameter"
                        size="sm"
                        variant="outline"
                        disabled={!formData.item_id || !row.operation_id || isLoadingRoute}
                        onClick={() => setParameterRowKey(row.key)}
                      />
                      <ActionButton
                        action="previous"
                        icon={ArrowUp}
                        iconOnly
                        size="sm"
                        type="button"
                        title="Move up"
                        aria-label="Move operation up"
                        disabled={index === 0 || isLoadingRoute}
                        onClick={() => handleMoveOperation(index, -1)}
                      />
                      <ActionButton
                        action="next"
                        icon={ArrowDown}
                        iconOnly
                        size="sm"
                        type="button"
                        title="Move down"
                        aria-label="Move operation down"
                        disabled={index === operationRows.length - 1 || isLoadingRoute}
                        onClick={() => handleMoveOperation(index, 1)}
                      />
                      <ActionButton
                        action="delete"
                        icon={Trash2}
                        iconOnly
                        size="sm"
                        type="button"
                        title="Remove"
                        aria-label="Remove operation"
                        disabled={isLoadingRoute}
                        onClick={() => handleRemoveOperation(row.key)}
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <OperationParameters
                        plantId={selectedPlantId}
                        itemId={formData.item_id}
                        operationId={row.operation_id}
                        drafts={row.parameters}
                        onRemove={(key) => handleRemoveParameter(row.key, key)}
                        disabled={isSaving || isLoadingRoute}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {errorMessage && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
                {errorMessage}
              </p>
            )}

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
              <ActionButton action="cancel" variant="outline" onClick={onClose} />
              <ActionButton
                action={isEdit ? "update" : "submit"}
                type="submit"
                isLoading={isSaving}
                disabled={isLoadingRoute || operationRows.some((row) => !row.operation_id)}
                label={isEdit ? "Update Item Routing" : "Create Item Routing"}
              />
            </div>
          </fieldset>
        </form>
        {parameterRow && (
          <ParameterDialog
            operationLabel={parameterRow.operation_id}
            onClose={() => setParameterRowKey(null)}
            onAdd={handleAddParameter}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ItemRouting = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const { selectedPlantId } = usePlantSelection();
  const [filterValues, setFilterValues] = useState({});
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const toast = useToast();

  const [loadItems, { data: itemData, isFetching: isLoadingItems }] =
    useLazyGetItemsQuery();
  const [loadItemRoutings] = useLazyGetItemRoutingsQuery();
  const [addItemRoutingSeries, { isLoading: isCreatingSeries }] =
    useAddItemRoutingSeriesMutation();
  const [updateItemRoutingSeries, { isLoading: isUpdatingSeries }] =
    useUpdateItemRoutingSeriesMutation();
  const [deleteItemRouting, { isLoading: isDeleting }] =
    useDeleteItemRoutingMutation();
  const [addRoutingParameter] = useAddRoutingParameterMutation();
  const [isSavingSeries, setIsSavingSeries] = useState(false);

  const resolvedFilterFields = useResolvedFilterFields(
    itemRoutingFilterFields,
    selectedPlantId
  );

  useEffect(() => {
    setFilterValues({});
    setPage(1);
  }, [selectedPlantId]);

  const listQueryArgs = useMemo(
    () => ({
      page,
      limit: rowsPerPage,
      ...filterValues,
      ...(selectedPlantId ? { plant_id: selectedPlantId } : {}),
    }),
    [filterValues, page, rowsPerPage, selectedPlantId]
  );

  const { data, isFetching } = useGetItemRoutingsQuery(listQueryArgs);
  const tableRows = useMemo(() => data?.data || [], [data?.data]);
  const responseMetaData = useMemo(() => data?.meta_data || {}, [data?.meta_data]);
  const totalRecords = data?.meta_data?.total_records ?? tableRows.length;

  const itemOptionsByPlant = useMemo(
    () =>
      buildOptionsByPlant(itemData?.data, "item_id", (item) => ({
        name: item?.item_name,
        code: item?.item_code,
        id: item?.item_id,
      })),
    [itemData?.data]
  );

  const loadFormOptions = useCallback(() => {
    if (!itemData && !isLoadingItems) {
      loadItems({ page: 1, limit: 1000 });
    }
  }, [isLoadingItems, itemData, loadItems]);

  const loadRouteForItem = useCallback(
    async (plantId, itemId) => {
      if (!plantId || !itemId) return [];

      const response = await loadItemRoutings({
        page: 1,
        limit: 1000,
        plant_id: plantId,
        item_id: itemId,
      }).unwrap();

      return response?.data || [];
    },
    [loadItemRoutings]
  );

  const rows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return tableRows;

    return tableRows.filter((row) =>
      searchFields.some((fieldName) =>
        String(getSearchValue(row, fieldName, responseMetaData))
          .toLowerCase()
          .includes(normalizedSearch)
      )
    );
  }, [responseMetaData, searchTerm, tableRows]);

  const searchedRecords = searchTerm ? rows.length : totalRecords;
  const hasAdvancedFilters = Object.values(filterValues).some(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );
  const hasActiveFilters = Boolean(searchTerm || selectedPlantId || hasAdvancedFilters);

  const columns = useMemo(
    () => [
      {
        name: "Routing ID",
        selector: (row) => row?.item_routing_id || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, responseMetaData),
        cell: (row) => renderPlantCell(row, responseMetaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Item",
        selector: (row) => getItemLabel(row, responseMetaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Operation",
        selector: (row) => getOperationLabel(row, responseMetaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Position",
        selector: (row) => row?.position ?? "-",
        sortable: true,
      },
    ],
    [responseMetaData]
  );

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilterValues((current) => ({
      ...current,
      [name]: value,
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setPage(1);
  };

  const handleAddClick = () => {
    loadFormOptions();
    setEditRow(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (row) => {
    loadFormOptions();
    setEditRow(row);
    setIsFormOpen(true);
  };

  const handleSaveSeries = async (payload, isUpdate, parameterDrafts) => {
    setIsSavingSeries(true);
    let routingSaved = false;
    try {
      const mutation = isUpdate ? updateItemRoutingSeries : addItemRoutingSeries;
      const response = assertSuccessfulResponse(
        await mutation(payload).unwrap(),
        "Unable to save item routing."
      );
      routingSaved = true;
      parameterDrafts.onRoutingSaved();
      for (const { key, saved, ...parameter } of parameterDrafts.parameters) {
        assertSuccessfulResponse(
          await addRoutingParameter(parameter).unwrap(),
          `Unable to save parameter ${parameter.parameter_name}.`
        );
        parameterDrafts.onParameterSaved(key);
      }

      toast.success(getResponseMessage(response, "Item routing saved successfully."));
      setIsFormOpen(false);
      setEditRow(null);
      setSearchTerm("");
    } catch (error) {
      const message = getRequestErrorMessage(error, "Unable to save item routing.");
      toast.error(routingSaved ? `Item routing saved, but some parameters could not be saved. Retry to save the remaining parameters. ${message}` : message);
    } finally {
      setIsSavingSeries(false);
    }
  };

  const handleConfirmDelete = async () => {
    const uid = getRecordUid(deleteTarget);
    if (!deleteTarget || !uid) return;

    try {
      const routeRows = await loadRouteForItem(
        deleteTarget.plant_id,
        deleteTarget.item_id
      );
      const remainingRows = routeRows
        .filter((row) => !isSameRoutingRecord(row, deleteTarget))
        .sort((first, second) => (first?.position || 0) - (second?.position || 0));

      let response;
      if (remainingRows.length) {
        response = assertSuccessfulResponse(
          await updateItemRoutingSeries(
            buildSeriesPayload({
              plantId: deleteTarget.plant_id,
              itemId: deleteTarget.item_id,
              operationRows: hydrateRouteRows(remainingRows),
            })
          ).unwrap(),
          "Unable to delete item routing."
        );
      } else {
        response = assertSuccessfulResponse(
          await deleteItemRouting(uid).unwrap(),
          "Unable to delete item routing."
        );
      }

      toast.success(getResponseMessage(response, "Item routing deleted successfully."));
      setDeleteTarget(null);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, "Unable to delete item routing."));
      setDeleteTarget(null);
    }
  };

  return (
    <div className="px-3 sm:px-5">
      <PageHeading title="Item Routing" />

      <div className="flex flex-col gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search item routings"
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <AdvancedFilters
            fields={resolvedFilterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            requirePlant
            hasPlant={Boolean(selectedPlantId)}
          />
          <ActionButton
            action="add"
            label="Add Item Routing"
            onClick={handleAddClick}
            className="w-full sm:w-auto"
            disabled={!selectedPlantId}
            title={!selectedPlantId ? "Select a plant from the top bar first" : undefined}
          />
        </div>
      </div>

      <MasterTable
        columns={columns}
        data={rows}
        entityName="Item Routing"
        isFetching={isFetching}
        noDataComponent={
          hasActiveFilters
            ? "No matching item routing found"
            : "No item routings found"
        }
        onDeleteClick={setDeleteTarget}
        onEditClick={handleEditClick}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        page={page}
        rowsPerPage={rowsPerPage}
        searchedRecords={searchedRecords}
        totalRecords={totalRecords}
      />

      <ItemRoutingSeriesDialog
        open={isFormOpen}
        sourceRow={editRow}
        selectedPlantId={selectedPlantId}
        itemOptionsByPlant={itemOptionsByPlant}
        isLoadingItems={isLoadingItems}
        onClose={() => {
          setIsFormOpen(false);
          setEditRow(null);
        }}
        onLoadOptions={loadFormOptions}
        onLoadRoute={loadRouteForItem}
        onSave={handleSaveSeries}
        isSaving={isSavingSeries || isCreatingSeries || isUpdatingSeries}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete item routing?"
        description={`${deleteTarget?.item_routing_id || "Item routing"} will be permanently deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting || isUpdatingSeries}
      />
    </div>
  );
};

export default ItemRouting;
